<template>
  <app-dropdown :title="$t('header.filters.tags.title')"
                class="tags-selector"
                ref="dropdown"
                @close="onDropdownClose"
  >
    <template #label>
      <div class="tags-selector__selected-tags">
        <app-tag v-for="filter in activeFilters"
                 :key="`${ filter.type }-${ filter.id }`"
                 readonly
                 :value="true"
                 :class="{'has-plus': showActiveFiltersPlus}"
        >
          <span v-html="getTextFromHtmlString(filter.name)"></span>
        </app-tag>
        <app-tag v-if="showActiveFiltersPlus"
                 readonly
                 :value="true"
                 class="tags-selector__selected-tags__plus"
        >
          {{ $t('header.filters.tags.selectedTagsPlus') }}
        </app-tag>
      </div>
    </template>

    <div class="tags-selector__dropdown">
      <tags-selector-list :title="$t('header.filters.tags.focus')"
                          :filters="focusFilters"
                          filter-type="focus"
      />
      <tags-selector-list :title="$t('header.filters.tags.street')"
                          :filters="streets"
                          filter-type="streets"
      />
      <tags-selector-list :title="$t('header.filters.tags.region')"
                          :filters="regions"
                          filter-type="regions"
      />
      <tags-selector-list :title="$t('header.filters.tags.type')"
                          :filters="types"
                          filter-type="types"
      />
      <tags-selector-list :title="$t('header.filters.tags.builder')"
                          :filters="builders"
                          filter-type="builders"
      />
    </div>

    <selector-controls @clear="clearFilters"
                       @apply="applyFilters"
                       :is-filter-active="isFilterActive"
    />

    <transition name="fade">
      <div class="tags-selector__is-loading-overlay"
           v-if="isActiveFiltersLoading"
      >
        <div class="loader"
        />
      </div>
    </transition>
  </app-dropdown>
</template>

<script>
import AppDropdown from "@/components/common/AppDropdown/AppDropdown";
import AppTag from "@/components/common/AppTag/AppTag";
import TagsSelectorList from "@/components/common/TheHeader/TagsSelector/TagsSelectorList";
import SelectorControls from "@/components/common/TheHeader/SelectorControls/SelectorControls";
import { mapState, mapGetters, mapActions } from "vuex";
import { ACTIVE_FILTERS_LABELS_COUNT } from "@/config/filters";
import getTextFromHtmlString from "@/helpers/getTextFromHtmlString";
import sortEntitiesByAlphabet from "@/helpers/sortEntitiesByName";

export default {
  name: "TagsSelector",
  components: {
    AppDropdown,
    AppTag,
    TagsSelectorList,
    SelectorControls
  },
  computed: {
    ...mapState('Filters', ['streets', 'regions', 'types', 'builders', 'isActiveFiltersLoading']),
    ...mapState('Filters', {
      'focusFilters': 'focus'
    }),
    ...mapGetters('Filters', ['activeTags']),

    activeFilters() {
      return sortEntitiesByAlphabet([...this.activeTags])
        .slice(0, ACTIVE_FILTERS_LABELS_COUNT)
    },
    showActiveFiltersPlus() {
      return this.activeTags.length > ACTIVE_FILTERS_LABELS_COUNT
    },
    isFilterActive() {
      return this.activeTags.length > 0
    }
  },
  methods: {
    hideDropdown() {
      this.$refs['dropdown'].hideDropdown()
    },
    onDropdownClose() {
      this.reloadCoordinates()
    },
    clearFilters() {
      this.resetAllFilters()
      this.hideDropdown()
    },
    applyFilters() {
      this.hideDropdown()
    },
    getTextFromHtmlString,

    ...mapActions('Coordinates', ['reloadCoordinates']),
    ...mapActions('Filters', ['resetAllFilters'])
  }
}
</script>

<style lang="scss">
.tags-selector {
  position: relative;

  &__selected-tags {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    height: calc(100% + 10px);
    max-height: calc(100% + 10px);
    margin-top: -5px;
    padding: 5px 0;
    max-width: calc(100% - 70px);

    .app-tag {
      margin: 1px 0 0 5px;
      font-size: .8em;
      max-width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;

      &.has-plus:nth-child(2) {
        max-width: calc(100% - 43px);
      }

      &__label {
        display: inline-block;
        text-overflow: ellipsis;
        max-width: 100%;
        line-height: 1em;
        overflow: hidden;
      }

      &:first-child {
        width: 100%;
        margin-left: 100%;
      }

    }

  }

  &__dropdown {
    max-height: 70vh;
    overflow-y: auto;
    padding: 22px 18px 28px 21px;
  }

  &__list {
    width: 560px;
    margin-bottom: 28px;
    max-width: 100%;
    flex-wrap: wrap;
    display: flex;
    flex-direction: row;

    &__title {
      flex-basis: 100%;
      font-size: 2.1em;
      font-weight: 500;
      margin-bottom: 12px;
    }

  }

  &__is-loading-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba($input-background-color, .6);
    display: flex;
    align-items: center;
    justify-content: center;

    .loader {
      width: 100px;
      height: 100px;
      border-width: 7px;
    }

  }

}

@include smallerThan("lg") {
  .tags-selector {
    &__list {
      width: 440px;
    }
  }
}

@include smallerThan("md") {
  .tags-selector {
    .app-dropdown__dropdown,
    &__dropdown {
      max-width: 100%;
    }
    &__dropdown {
      max-height: calc(100vh - 320px);
    }
    &__list {
      width: 100%;
    }
  }
}
</style>
